import {
  Program,
  Invitation,
  Organization,
  User,
  AuthToken,
  PhaseAction,
  Circle,
} from "./types/platform.types";
import { makeContext } from "@shared/utils/makeContext";

// --- Name ---
const NAME = "Platform";

// --- State & Actions ---
export type PlatformState = {
  organization?: Organization | null;
  invitation?: Invitation | null;
  program?: Program | null;
  user?: User | null;
  authToken?: AuthToken | null;
  isTokenExpired?: boolean | null;
  isLoading?: boolean | null;
  isHydrated?: boolean | null;
  isSignupDisabled?: boolean | null;
  isPlatformClosed?: boolean | null;
  isChatDisabled?: boolean | null;
  isAddToCalendarDisabled?: boolean | null;
  isWorkbookDownloadDisabled?: boolean | null;
  isTranscriptionDisabled?: boolean | null;
  userPhaseActions?: PhaseAction[] | null;
  userCircles?: Circle[] | null;
  alertMessage: string | undefined;
};

export type PlatformAction = { type: string; payload?: any };

export const INVITATION_SET = "invitation/set";
export const USER_SET = "user/set";
export const TOKEN_EXPIRED_SET = "token/expired/set";
export const PLATFORM_SET_LOADING = "platform/set/loading";
export const PLATFORM_SET_CLOSED = "platform/set/closed";
export const PLATFORM_RESET = "platform/reset";
export const USER_PHASE_ACTIONS_SET = "user/phase/actions/set";
export const USER_CIRCLES_SET = "user/circles/set";

export const ACTION_TYPES = {
  INVITATION_SET,
  USER_SET,
  PLATFORM_SET_LOADING,
  PLATFORM_SET_CLOSED,
  PLATFORM_RESET,
  USER_PHASE_ACTIONS_SET,
  USER_CIRCLES_SET,
} as const;

export const initialPlatform: PlatformState = {
  organization: undefined,
  invitation: undefined,
  program: undefined,
  user: undefined,
  authToken: undefined,
  isTokenExpired: false,
  isLoading: false,
  isHydrated: false,
  isSignupDisabled: true, // Hardcoded to disable signup
  isPlatformClosed: true, // Hardcoded to close platform access
  isChatDisabled: false, // Hardcoded to enable chat
  isAddToCalendarDisabled: false, // Hardcoded to enable add to calendar
  isWorkbookDownloadDisabled: false, // Hardcoded to disable workbook download
  isTranscriptionDisabled: false, // Hardcoded to disable transcription
  alertMessage: undefined,
};

// --- Reducer ---
export function platformReducer(
  state: PlatformState,
  action: PlatformAction
): PlatformState {
  switch (action.type) {
    case INVITATION_SET:
      return {
        ...state,
        invitation: {
          code: action.payload?.code,
          id: parseInt(action.payload?.id, 10),
          is_valid: action.payload?.is_valid ?? true,
          is_used: action.payload?.is_used ?? false,
        },
        organization: {
          name: action.payload?.organization?.name,
          id: action.payload?.organization?.id,
        },
        program: {
          id: action.payload?.program?.id,
          name: action.payload?.program?.name,
        },
      };

    case USER_SET:
      return {
        ...state,
        user: action.payload.user,
        authToken: action.payload.authToken || state.authToken,
      };

    case TOKEN_EXPIRED_SET:
      return {
        ...state,
        isTokenExpired: action.payload,
      };

    case PLATFORM_SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case PLATFORM_SET_CLOSED:
      return {
        ...state,
        isPlatformClosed: action.payload,
      };

    case PLATFORM_RESET:
      return initialPlatform;

    case USER_PHASE_ACTIONS_SET:
      return {
        ...state,
        userPhaseActions: action.payload.userPhaseActions,
      };

    case USER_CIRCLES_SET:
      return {
        ...state,
        userCircles: action.payload.userCircles,
      };

    default:
      throw new Error(`Unknown action: ${action.type}`);
  }
}

// --- Generate Context ---
export const {
  Provider: PlatformProvider,
  useState: usePlatform,
  useDispatch: usePlatformDispatch,
} = makeContext<PlatformState, PlatformAction>({
  name: NAME,
  initialState: initialPlatform,
  reducer: platformReducer,
  storageKey: "polpltd",
  syncedKeys: ["program", "invitation", "organization", "user", "authToken"],
});
